import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/user-dashboard/api';
import { DashboardFarmlandMapItem } from '@/api/user-dashboard/type';

// ── 작물명 → 이미지 경로 매핑
const CROP_IMAGE_MAP: Record<string, string> = {
    옥수수: '/images/crop/corn.png',
    감자: '/images/crop/potato.png',
    벼: '/images/crop/rice.png',
    콩: '/images/crop/soybean.png',
    잔디: '/images/crop/bermudagrass.png',
};
const DEFAULT_CROP_IMG = '/images/crop/default.png';
const getCropImg = (cropName: string) =>
    CROP_IMAGE_MAP[cropName] ?? DEFAULT_CROP_IMG;

const getProgressColor = (p: number): string => {
    if (p >= 80) return '#2dbe6c';
    if (p >= 50) return '#f59e0b';
    return '#ef4444';
};
const getProgressClass = (p: number): string => {
    if (p >= 80) return 'success';
    if (p >= 50) return 'warning';
    if (p > 0) return 'danger';
    return 'secondary';
};

interface FarmMarker {
    id: number;
    name: string;
    lat: number;
    lng: number;
    area: string;
    crop: string;
    cropImg: string;
    progress: number;
    tasks: number;
    status: 'active' | 'rest';
    isUrgent: boolean;
    pdat: string | null;
    hdat: string | null;
    currentDap: number | null;
    growthStageName: string | null;
    feature: any | null;
    scheduleIdx: number | null;
}

const toFarmMarker = (item: DashboardFarmlandMapItem): FarmMarker => ({
    id: item.gfIdx,
    name: item.farmlandName,
    lat: parseFloat(item.lat),
    lng: parseFloat(item.lon),
    area: `${item.areaPyeong}평`,
    crop: item.cropName ?? '-',
    cropImg: getCropImg(item.cropName),
    progress: item.progress ?? 0,
    tasks: item.taskCnt ?? 0,
    status: item.growthFlag === 1 ? 'active' : 'rest',
    isUrgent: item.isUrgent === 1,
    pdat: item.pdat ?? null,
    hdat: item.hdat ?? null,
    currentDap: item.currentDap ?? null,
    growthStageName: item.growthStageName ?? null,
    feature: item.feature ? JSON.parse(item.feature) : null,
    scheduleIdx: item.scheduleIdx ?? null,
});

const LEGENDS = [
    { color: '#2dbe6c', label: '생육 양호 (80%+)' },
    { color: '#f59e0b', label: '생육 보통 (50~79%)' },
    { color: '#ef4444', label: '생육 관심 (~49%)' },
    { color: '#94a3b8', label: '휴경' },
];

const overlayBtnBase: React.CSSProperties = {
    width: 32,
    height: 32,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 700,
    transition: 'background 0.15s, color 0.15s',
    background: '#fff',
    color: '#444',
};

interface SideFarmItemProps {
    farm: FarmMarker;
    onClick: (farm: FarmMarker) => void;
}

const SideFarmItem = ({ farm, onClick }: SideFarmItemProps) => {
    const colorClass = getProgressClass(farm.progress);
    return (
        <div
            className="d-flex align-items-start gap-2 p-2 rounded-3 mb-2"
            style={{
                background: '#fff',
                border: '1px solid #eef0f5',
                cursor: 'pointer',
                transition: 'box-shadow 0.15s',
            }}
            onClick={() => onClick(farm)}
            onMouseEnter={e =>
                ((e.currentTarget as HTMLDivElement).style.boxShadow =
                    '0 2px 12px rgba(0,0,0,0.10)')
            }
            onMouseLeave={e =>
                ((e.currentTarget as HTMLDivElement).style.boxShadow = 'none')
            }
        >
            <div
                className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                    width: 38,
                    height: 38,
                    background:
                        farm.status === 'active' ? '#f0fdf4' : '#f8f9fa',
                    border: `1.5px solid ${farm.status === 'active' ? '#bbf7d0' : '#e9ecef'}`,
                    overflow: 'hidden',
                }}
            >
                <img
                    src={farm.cropImg}
                    alt={farm.crop}
                    style={{ width: 26, height: 26, objectFit: 'contain' }}
                    onError={e => {
                        (e.currentTarget as HTMLImageElement).src =
                            DEFAULT_CROP_IMG;
                    }}
                />
            </div>

            <div className="flex-fill" style={{ minWidth: 0 }}>
                <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="fw-bold" style={{ fontSize: 13 }}>
                        {farm.name}
                    </span>
                    <span
                        className="badge bg-warning-transparent text-warning rounded-pill flex-shrink-0 ms-1"
                        style={{
                            fontSize: 10,
                            visibility: farm.tasks > 0 ? 'visible' : 'hidden',
                        }}
                    >
                        <i
                            className={`${farm.isUrgent ? 'ri-alarm-warning-line' : 'ri-tools-line'} me-1`}
                        ></i>
                        {farm.tasks}건
                    </span>
                </div>

                <div className="d-flex align-items-center gap-1 mb-1 flex-wrap">
                    <span className="text-muted" style={{ fontSize: 11 }}>
                        {farm.crop} · {farm.area}
                    </span>
                    <span
                        className={`badge rounded-pill bg-${farm.status === 'active' ? 'success' : 'secondary'}-transparent text-${farm.status === 'active' ? 'success' : 'secondary'} fs-10`}
                    >
                        {farm.status === 'active' ? '● 관리중' : '○ 휴경'}
                    </span>
                </div>

                {farm.progress > 0 && (
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <div className="flex-fill">
                                <div
                                    className="progress p-0"
                                    style={{ height: 5, borderRadius: 99 }}
                                >
                                    <div
                                        className={`progress-bar bg-${colorClass}`}
                                        style={{
                                            width: `${farm.progress}%`,
                                            borderRadius: 99,
                                        }}
                                    />
                                </div>
                            </div>
                            <span
                                className={`text-${colorClass} fw-bold`}
                                style={{
                                    fontSize: 11,
                                    minWidth: 28,
                                    textAlign: 'right',
                                }}
                            >
                                {farm.progress}%
                            </span>
                        </div>
                        {(farm.pdat || farm.hdat) && (
                            <div
                                className="d-flex gap-2"
                                style={{ fontSize: 10, color: '#aaa' }}
                            >
                                {farm.pdat && <span>파종 {farm.pdat}</span>}
                                {farm.hdat && <span>수확 {farm.hdat}</span>}
                                {farm.currentDap != null && (
                                    <span>DAP {farm.currentDap}일</span>
                                )}
                            </div>
                        )}
                        {farm.growthStageName && (
                            <span className="badge bg-light text-muted fs-10 mt-1">
                                {farm.growthStageName}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ── 메인 컴포넌트
const AgencyFarmMap = () => {
    const mapRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    const naverMapRef = useRef<any>(null);
    const infoWindowsRef = useRef<any[]>([]);
    const markersRef = useRef<any[]>([]);
    const polygonsRef = useRef<any[]>([]);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [panelOpen, setPanelOpen] = useState(true);
    const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'rest'>(
        'all',
    );
    const [mapType, setMapType] = useState<'normal' | 'satellite'>('satellite');
    const [farmList, setFarmList] = useState<FarmMarker[]>([]);
    const [loading, setLoading] = useState(true);

    const PANEL_WIDTH = 300;

    const filteredList = farmList.filter(f => {
        if (filterStatus === 'active') return f.status === 'active';
        if (filterStatus === 'rest') return f.status === 'rest';
        return true;
    });

    // ── API 로드
    useEffect(() => {
        api.dashboardFarmlandMapList()
            .then(data => setFarmList(data.map(toFarmMarker)))
            .catch(e => console.error('농지 목록 조회 실패', e))
            .finally(() => setLoading(false));
    }, []);

    // ── 지도 타입 전환
    const handleMapType = (type: 'normal' | 'satellite') => {
        setMapType(type);
        if (!naverMapRef.current || !window.naver?.maps) return;
        naverMapRef.current.setMapTypeId(
            type === 'satellite'
                ? window.naver.maps.MapTypeId.HYBRID
                : window.naver.maps.MapTypeId.NORMAL,
        );
    };

    // ── 커스텀 줌
    const handleZoom = (direction: 'in' | 'out') => {
        if (!naverMapRef.current) return;
        const current = naverMapRef.current.getZoom();
        naverMapRef.current.setZoom(
            direction === 'in' ? current + 1 : current - 1,
        );
    };

    // ── InfoWindow 내용 생성
    const buildInfoWindowContent = (farm: FarmMarker): string => {
        const color = getProgressColor(farm.progress);
        return `
            <div style="font-family:'Noto Sans KR',sans-serif;min-width:230px;padding:14px 16px;border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.15);background:#fffffffe;border-top:4px solid ${color};position:relative;">
                <button onclick="window.__closeIw_${farm.id}?.()"
                    style="position:absolute;top:8px;right:8px;background:none;border:none;cursor:pointer;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#aaa;font-size:16px;line-height:1;"
                    onmouseover="this.style.background='#f1f5f9';this.style.color='#333'"
                    onmouseout="this.style.background='none';this.style.color='#aaa'">✕</button>

                <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;padding-right:24px;">
                    <div style="width:36px;height:36px;border-radius:8px;background:#f0fdf4;display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0;">
                        <img src="${farm.cropImg}" alt="${farm.crop}"
                             style="width:26px;height:26px;object-fit:contain;"
                             onerror="this.src='${DEFAULT_CROP_IMG}'" />
                    </div>
                    <div>
                        <div style="font-weight:700;font-size:14px;color:#1a1a2e">${farm.name}</div>
                        <div style="display:flex;align-items:center;gap:4px;margin-top:2px;flex-wrap:wrap;">
                            <span style="font-size:11px;color:#888;">${farm.crop} | ${farm.area}</span>
                            <span style="font-size:11px;color:#888;">|</span>
                            <span style="display:inline-block;background:${farm.status === 'active' ? '#dcfce7' : '#f1f5f9'};color:${farm.status === 'active' ? '#16a34a' : '#94a3b8'};font-size:10px;font-weight:600;padding:1px 7px;border-radius:99px;">
                                ${farm.status === 'active' ? '● 관리중' : '○ 휴경'}
                            </span>
                        </div>
                    </div>
                </div>

                <div style="background:#f8f9fa;border-radius:8px;padding:8px 10px;margin-bottom:8px;">
                    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:5px;">
                        <span style="font-size:11px;color:#666;">
                            생육진행도
                            ${farm.growthStageName ? `<span style="margin-left:4px;font-size:10px;color:#25a244;">[${farm.growthStageName}]</span>` : ''}
                        </span>
                        <span style="font-size:12px;font-weight:700;color:${color}">${farm.progress}%</span>
                    </div>
                    <div style="background:#e9ecef;border-radius:99px;height:6px;overflow:hidden;">
                        <div style="width:${farm.progress}%;height:100%;background:${color};border-radius:99px;"></div>
                    </div>
                    ${
                        farm.pdat || farm.hdat
                            ? `
                    <div style="display:flex;gap:8px;margin-top:5px;font-size:10px;color:#aaa;">
                        ${farm.pdat ? `<span>파종 ${farm.pdat}</span>` : ''}
                        ${farm.hdat ? `<span>수확 ${farm.hdat}</span>` : ''}
                        ${farm.currentDap != null ? `<span>DAP ${farm.currentDap}일</span>` : ''}
                    </div>`
                            : ''
                    }
                </div>

                <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:${farm.progress >= 1 ? '10px' : '0'};">
                    <div style="background:${farm.isUrgent ? '#fff0f0' : '#f0f9f4'};border-radius:7px;padding:6px 8px;text-align:center;">
                        <div style="font-size:10px;color:#888;">추천농작업(7일)</div>
                        <div style="font-size:12px;font-weight:600;color:${farm.tasks > 0 ? (farm.isUrgent ? '#ef4444' : '#f59e0b') : '#888'};">
                            ${farm.tasks > 0 ? (farm.isUrgent ? '⚠️ ' : '') + farm.tasks + '건' : '없음'}
                        </div>
                    </div>
                    <div style="background:#f0f9f4;border-radius:7px;padding:6px 8px;text-align:center;">
                        <div style="font-size:10px;color:#888;">관리상태</div>
                        <div style="font-size:12px;font-weight:600;color:${farm.status === 'active' ? '#16a34a' : '#94a3b8'};">
                            ${farm.status === 'active' ? '관리중' : '휴경'}
                        </div>
                    </div>
                </div>

                ${
                    farm.progress >= 1
                        ? `
                <button
                    onclick="window.__goGrowthDashboard_${farm.id}?.()"
                    style="
                        width:100%;padding:8px 0;
                        background:#25a244;color:#fff;
                        border:none;border-radius:8px;
                        font-size:12px;font-weight:600;
                        cursor:pointer;
                        display:flex;align-items:center;justify-content:center;gap:6px;
                        box-shadow:0 2px 6px rgba(37,162,68,0.3);
                    "
                    onmouseover="this.style.background='#1e8e38'"
                    onmouseout="this.style.background='#25a244'"
                >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                    </svg>
                    생육 대시보드 바로가기
                </button>`
                        : ''
                }
            </div>
        `;
    };

    // ── 패널 농지 클릭 → 마커 InfoWindow 열기
    const handleFarmClick = useCallback(
        (farm: FarmMarker) => {
            if (!naverMapRef.current) return;
            const idx = farmList.findIndex(f => f.id === farm.id);
            if (idx < 0) return;
            infoWindowsRef.current.forEach(iw => iw.close());
            infoWindowsRef.current[idx]?.open(
                naverMapRef.current,
                markersRef.current[idx],
            );
            naverMapRef.current.setCenter(
                new window.naver.maps.LatLng(farm.lat, farm.lng),
            );
            naverMapRef.current.setZoom(17);
        },
        [farmList],
    );

    // ── 폴리곤 그리기
    const drawFarmPolygons = useCallback(
        (map: any, farms: FarmMarker[]) => {
            polygonsRef.current.forEach(p => p?.setMap(null));
            polygonsRef.current = [];

            farms.forEach(farm => {
                if (!farm.feature?.coordinates) return;
                const color = getProgressColor(farm.progress);
                const polygonPaths: any[] = [];

                farm.feature.coordinates.forEach((polygon: number[][][]) => {
                    const exteriorRing = polygon[0];
                    const path = exteriorRing.map(
                        (coord: number[]) =>
                            new window.naver.maps.LatLng(coord[1], coord[0]),
                    );
                    polygonPaths.push(path);
                });

                const fillColor = farm.status === 'active' ? color : '#94a3b8';
                const strokeColor =
                    farm.status === 'active' ? color : '#94a3b8';

                const polygon = new window.naver.maps.Polygon({
                    map,
                    paths: polygonPaths,
                    fillColor,
                    fillOpacity: 0.3,
                    strokeColor,
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    strokeStyle: farm.status === 'active' ? 'solid' : 'dash',
                    clickable: true,
                    zIndex: 2,
                });

                window.naver.maps.Event.addListener(polygon, 'mouseover', () =>
                    polygon.setOptions({ fillOpacity: 0.55 }),
                );
                window.naver.maps.Event.addListener(polygon, 'mouseout', () =>
                    polygon.setOptions({ fillOpacity: 0.3 }),
                );
                window.naver.maps.Event.addListener(polygon, 'click', () =>
                    handleFarmClick(farm),
                );

                polygonsRef.current.push(polygon);
            });
        },
        [handleFarmClick],
    );

    // ── 마커 등록
    const drawMarkers = useCallback((map: any, farms: FarmMarker[]) => {
        infoWindowsRef.current.forEach(iw => iw?.close());
        markersRef.current.forEach(m => m?.setMap(null));
        infoWindowsRef.current = [];
        markersRef.current = [];
        farms.forEach(farm => {
            delete (window as any)[`__closeIw_${farm.id}`];
            delete (window as any)[`__goGrowthDashboard_${farm.id}`];
        });

        farms.forEach((farm, i) => {
            const color = getProgressColor(farm.progress);

            const markerContent = `
                    <div style="position:relative;cursor:pointer;display:flex;flex-direction:column;align-items:center;">
                        <div style="
                            background:${farm.status === 'active' ? color : '#94a3b8'};
                            border-radius:50%;width:36px;height:36px;
                            display:flex;align-items:center;justify-content:center;
                            box-shadow:0 2px 8px rgba(0,0,0,0.25);border:2.5px solid #fff;
                            overflow:hidden;
                        ">
                            <img src="${farm.cropImg}" alt="${farm.crop}"
                                 style="width:22px;height:22px;object-fit:contain;"
                                 onerror="this.src='${DEFAULT_CROP_IMG}'" />
                        </div>
                        <div style="
                            background:${farm.status === 'active' ? color : '#94a3b8'};
                            color:#fff;font-size:10px;font-weight:700;
                            padding:1px 6px;border-radius:4px;margin-top:2px;
                            white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,0.18);
                        ">${farm.name}</div>
                        ${
                            farm.tasks > 0
                                ? `
                        <div style="
                            position:absolute;top:-4px;right:-4px;
                            background:${farm.isUrgent ? '#ef4444' : '#f59e0b'};
                            color:#fff;border-radius:50%;width:16px;height:16px;
                            display:flex;align-items:center;justify-content:center;
                            font-size:9px;font-weight:700;border:1.5px solid #fff;
                        ">${farm.tasks}</div>`
                                : ''
                        }
                    </div>
                `;

            const marker = new window.naver.maps.Marker({
                position: new window.naver.maps.LatLng(farm.lat, farm.lng),
                map,
                icon: {
                    content: markerContent,
                    anchor: new window.naver.maps.Point(18, 44),
                },
            });

            const infoWindow = new window.naver.maps.InfoWindow({
                content: buildInfoWindowContent(farm),
                borderWidth: 0,
                backgroundColor: 'transparent',
                disableAnchor: true,
                padding: 0,
                pixelOffset: new window.naver.maps.Point(0, -10),
            });

            // ── 전역 함수 등록
            (window as any)[`__closeIw_${farm.id}`] = () => infoWindow.close();

            (window as any)[`__goGrowthDashboard_${farm.id}`] = () => {
                infoWindow.close();
                navigateRef.current(`/growth/dashboard/${farm.id}`);
            };

            window.naver.maps.Event.addListener(marker, 'click', () => {
                infoWindowsRef.current.forEach(iw => iw.close());
                infoWindow.open(map, marker);
            });

            markersRef.current[i] = marker;
            infoWindowsRef.current[i] = infoWindow;
        });
    }, []);

    // ── farmList 로드 완료 후 마커/폴리곤 + 첫 농지 자동 선택
    useEffect(() => {
        if (!mapLoaded || !naverMapRef.current || farmList.length === 0) return;

        const map = naverMapRef.current;

        drawMarkers(map, farmList);
        drawFarmPolygons(map, farmList);

        // 첫 번째 농지 센터 이동 + InfoWindow 자동 오픈
        setTimeout(() => {
            const firstFarm = farmList[0];
            map.setCenter(
                new window.naver.maps.LatLng(firstFarm.lat, firstFarm.lng),
            );
            map.setZoom(15);

            if (markersRef.current[0] && infoWindowsRef.current[0]) {
                infoWindowsRef.current[0].open(map, markersRef.current[0]);
            }
        }, 300);
    }, [farmList, mapLoaded]);

    // ── 지도 초기화
    const initializeMap = useCallback(() => {
        if (!mapRef.current || !window.naver?.maps) return;
        try {
            const map = new window.naver.maps.Map(mapRef.current, {
                center: new window.naver.maps.LatLng(37.5666805, 126.9784147),
                zoom: 13,
                minZoom: 6,
                maxZoom: 21,
                mapTypeId: window.naver.maps.MapTypeId.HYBRID,
                mapDataControl: false,
                scaleControl: false,
                mapTypeControl: false,
                zoomControl: false,
            });

            naverMapRef.current = map;

            setTimeout(() => {
                mapRef.current
                    ?.querySelectorAll<HTMLElement>(
                        '.naver-logo, [class*="logo_"], a[href*="naver.com"]',
                    )
                    .forEach(el => {
                        el.style.opacity = '0.15';
                    });
            }, 800);

            const cadastral = new window.naver.maps.CadastralLayer();
            cadastral.setMap(map);

            setMapLoaded(true);
        } catch (error) {
            console.error('지도 초기화 오류:', error);
        }
    }, []);

    useEffect(() => {
        const load = () => {
            if (window.naver?.maps) {
                initializeMap();
                return;
            }
            const existing = document.querySelector(
                'script[src*="openapi.map.naver.com"]',
            );
            if (existing) existing.remove();
            const clientId =
                (import.meta as any).env?.VITE_NCP_API_KEY_ID ?? '';
            const script = document.createElement('script');
            script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}&submodules=geocoder,drawing,geometry`;
            script.async = true;
            script.onload = () => setTimeout(initializeMap, 200);
            document.head.appendChild(script);
        };
        load();
        return () => {
            farmList.forEach(farm => {
                delete (window as any)[`__closeIw_${farm.id}`];
                delete (window as any)[`__goGrowthDashboard_${farm.id}`];
            });
            polygonsRef.current.forEach(p => p?.setMap(null));
        };
    }, [initializeMap]);

    const overlayBox: React.CSSProperties = {
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
        border: '1px solid #e2e8f0',
    };

    return (
        <div className="card custom-card mb-3 overflow-hidden">
            {/* 카드 헤더 */}
            <div className="card-header justify-content-between">
                <div className="card-title">
                    <i className="ri-map-2-line me-1 text-success"></i>농지 지도
                </div>
                <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-success-transparent text-success fs-11">
                        <i className="ri-map-pin-line me-1"></i>
                        {farmList.filter(f => f.status === 'active').length}개
                        관리중
                    </span>
                    <span className="badge bg-secondary-transparent text-secondary fs-11">
                        {farmList.filter(f => f.status === 'rest').length}개
                        휴경
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-success-light btn-wave"
                    >
                        <i className="ri-focus-3-line me-1"></i>전체 보기
                    </button>
                </div>
            </div>

            {/* 범례 */}
            <div
                className="px-3 py-2 d-flex flex-wrap gap-3 align-items-center"
                style={{
                    background: '#f8f9fa',
                    borderBottom: '1px solid #eee',
                    fontSize: 11,
                }}
            >
                <span className="text-muted fw-medium">범례:</span>
                {LEGENDS.map((l, i) => (
                    <span key={i} className="d-flex align-items-center gap-1">
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: l.color,
                                display: 'inline-block',
                            }}
                        />
                        {l.label}
                    </span>
                ))}
                <span className="ms-auto text-muted">
                    <i className="ri-information-line me-1"></i>마커 클릭 시
                    상세 정보 확인
                </span>
            </div>

            {/* 지도 영역 */}
            <div style={{ position: 'relative' }}>
                {/* 로딩 */}
                {(!mapLoaded || loading) && (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                            height: 700,
                            background: '#f8f9fa',
                            position: 'absolute',
                            inset: 0,
                            zIndex: 10,
                        }}
                    >
                        <div className="text-center text-muted">
                            <div
                                className="spinner-border text-success mb-2"
                                style={{ width: 28, height: 28 }}
                            />
                            <div style={{ fontSize: 13 }}>
                                지도를 불러오는 중...
                            </div>
                        </div>
                    </div>
                )}

                {/* 지도 */}
                <div ref={mapRef} style={{ height: 700 }} />

                {mapLoaded && (
                    <>
                        {/* 원형 패널 토글 플로팅 버튼 */}
                        <button
                            type="button"
                            onClick={() => setPanelOpen(true)}
                            title="농지 목록 열기"
                            style={{
                                position: 'absolute',
                                top: 10,
                                left: 10,
                                zIndex: 25,
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                border: 'none',
                                cursor: panelOpen ? 'default' : 'pointer',
                                background: '#fff',
                                color: '#25a244',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.22)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: panelOpen ? 0 : 1,
                                visibility: panelOpen ? 'hidden' : 'visible',
                                transition: panelOpen
                                    ? 'opacity 0.2s ease, visibility 0.2s ease'
                                    : 'opacity 0.6s ease, visibility 0.6s ease',
                                pointerEvents: panelOpen ? 'none' : 'auto',
                            }}
                            onMouseEnter={e => {
                                if (!panelOpen)
                                    (
                                        e.currentTarget as HTMLButtonElement
                                    ).style.background = '#f0fdf4';
                            }}
                            onMouseLeave={e => {
                                if (!panelOpen)
                                    (
                                        e.currentTarget as HTMLButtonElement
                                    ).style.background = '#fff';
                            }}
                        >
                            <i
                                className="ri-layout-left-line"
                                style={{ fontSize: 16 }}
                            />
                        </button>

                        {/* 줌 버튼 */}
                        <div
                            style={{
                                position: 'absolute',
                                bottom: 40,
                                right: 10,
                                zIndex: 25,
                                display: 'flex',
                                flexDirection: 'column',
                                ...overlayBox,
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => handleZoom('in')}
                                style={{
                                    ...overlayBtnBase,
                                    borderBottom: '1px solid #e2e8f0',
                                    borderRadius: 0,
                                    fontSize: 20,
                                }}
                                onMouseEnter={e => {
                                    (
                                        e.currentTarget as HTMLButtonElement
                                    ).style.background = '#f0fdf4';
                                    (
                                        e.currentTarget as HTMLButtonElement
                                    ).style.color = '#25a244';
                                }}
                                onMouseLeave={e => {
                                    (
                                        e.currentTarget as HTMLButtonElement
                                    ).style.background = '#fff';
                                    (
                                        e.currentTarget as HTMLButtonElement
                                    ).style.color = '#444';
                                }}
                            >
                                +
                            </button>
                            <button
                                type="button"
                                onClick={() => handleZoom('out')}
                                style={{
                                    ...overlayBtnBase,
                                    borderRadius: 0,
                                    fontSize: 20,
                                }}
                                onMouseEnter={e => {
                                    (
                                        e.currentTarget as HTMLButtonElement
                                    ).style.background = '#f0fdf4';
                                    (
                                        e.currentTarget as HTMLButtonElement
                                    ).style.color = '#25a244';
                                }}
                                onMouseLeave={e => {
                                    (
                                        e.currentTarget as HTMLButtonElement
                                    ).style.background = '#fff';
                                    (
                                        e.currentTarget as HTMLButtonElement
                                    ).style.color = '#444';
                                }}
                            >
                                −
                            </button>
                        </div>

                        {/* 일반/위성 토글 */}
                        <div
                            style={{
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                zIndex: 25,
                                ...overlayBox,
                                display: 'flex',
                            }}
                        >
                            {(['normal', 'satellite'] as const).map(type => (
                                <button
                                    key={type}
                                    type="button"
                                    onClick={() => handleMapType(type)}
                                    style={{
                                        padding: '5px 14px',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        border: 'none',
                                        cursor: 'pointer',
                                        background:
                                            mapType === type
                                                ? '#25a244'
                                                : '#fff',
                                        color:
                                            mapType === type ? '#fff' : '#555',
                                        transition:
                                            'background 0.15s, color 0.15s',
                                        letterSpacing: '-0.2px',
                                    }}
                                >
                                    {type === 'normal' ? '일반' : '위성'}
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {/* 왼쪽 슬라이드 패널 */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: PANEL_WIDTH,
                        zIndex: 20,
                        transform: panelOpen
                            ? 'translateX(0)'
                            : 'translateX(-100%)',
                        transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
                        display: 'flex',
                        flexDirection: 'column',
                        background: '#fff',
                        boxShadow: panelOpen
                            ? '4px 0 24px rgba(0,0,0,0.13)'
                            : 'none',
                        borderRight: '1px solid #eef0f5',
                    }}
                >
                    {/* 패널 헤더 */}
                    <div
                        className="d-flex align-items-center justify-content-between px-3 py-2 flex-shrink-0"
                        style={{
                            borderBottom: '1px solid #eef0f5',
                            background: '#f8f9fa',
                        }}
                    >
                        <div className="fw-bold fs-13 d-flex align-items-center gap-2">
                            <i className="ri-landscape-line text-success"></i>
                            농지 목록
                            <span className="badge bg-success-transparent text-success fs-10">
                                {farmList.length}필지
                            </span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-light"
                            onClick={() => setPanelOpen(false)}
                            style={{ padding: '2px 8px' }}
                        >
                            <i className="ri-close-line"></i>
                        </button>
                    </div>

                    {/* 필터 탭 */}
                    <div
                        className="d-flex gap-1 px-3 py-2 flex-shrink-0"
                        style={{ borderBottom: '1px solid #eef0f5' }}
                    >
                        {(['all', 'active', 'rest'] as const).map(key => (
                            <button
                                key={key}
                                type="button"
                                className={`btn btn-sm btn-wave flex-fill ${filterStatus === key ? 'btn-success' : 'btn-success-light'}`}
                                style={{ fontSize: 11 }}
                                onClick={() => setFilterStatus(key)}
                            >
                                {key === 'all'
                                    ? '전체'
                                    : key === 'active'
                                      ? '관리중'
                                      : '휴경'}
                            </button>
                        ))}
                    </div>

                    {/* 목록 */}
                    <div
                        className="px-3 py-2"
                        style={{
                            overflowY: 'auto',
                            flex: 1,
                            scrollbarWidth: 'thin',
                        }}
                    >
                        {loading ? (
                            <div className="text-center text-muted py-4">
                                <div className="spinner-border spinner-border-sm text-success mb-2" />
                                <div style={{ fontSize: 12 }}>로딩중...</div>
                            </div>
                        ) : filteredList.length > 0 ? (
                            filteredList.map(farm => (
                                <SideFarmItem
                                    key={farm.id}
                                    farm={farm}
                                    onClick={handleFarmClick}
                                />
                            ))
                        ) : (
                            <div
                                className="text-center text-muted py-5"
                                style={{ fontSize: 13 }}
                            >
                                <div style={{ fontSize: 28, marginBottom: 8 }}>
                                    🌿
                                </div>
                                해당하는 농지가 없습니다.
                            </div>
                        )}
                    </div>

                    {/* 푸터 */}
                    <div
                        className="px-3 py-2 flex-shrink-0"
                        style={{ borderTop: '1px solid #eef0f5' }}
                    >
                        <button
                            type="button"
                            className="btn btn-sm btn-light btn-wave w-100"
                            style={{ fontSize: 12 }}
                            onClick={() => navigate('/farmland')}
                        >
                            <i className="ri-add-line me-1"></i>농지 추가 등록
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgencyFarmMap;
